import { useEffect, useMemo, useState } from "react";
import { makeQueryString } from "core/makeQueryString";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import {
  ScalarsHiAggregation,
  ScalarsHiDataSlice
} from "interfaces/scalars-hi";

/**
 * @property {boolean} loading whether the chart data is currently being fetched.
 * @property {boolean} error whether an error occurred during the fetching of the chart data.
 * @property {Array<ScalarsHiDataSlice> | undefined} data a list of scalar hi data slices that can be used to generate a chart. Undefined if loading or an error occurred.
 * @property {boolean} isRaw whether the data aggregation being used is currently set to raw
 */
export interface GetAlertChartDataResponse {
  loading: boolean;
  error: boolean;
  data: ScalarsHiDataSlice[] | undefined;
  isRaw: boolean;
}

interface ScalarsHiFetchResponse {
  loading: boolean;
  error: boolean;
  response: { results: ScalarsHiDataSlice[]; count: number } | undefined;
}

/**
 * A hook to get scalars hi data given a specific tag and machine id.
 * @param {number} tag_id
 * @param {number} machine_id
 * @param aggregation the granularity of the aggregation, defaults to Raw.
 * @returns Charts data / Loading State / Error State
 */
export const useGetAlertChartData = (
  tag_id: number,
  machine_id: number,
  aggregation: ScalarsHiAggregation | null = null
): GetAlertChartDataResponse => {
  // used to determine what format the data is currently being sent back in
  // needed because we switch between raw and aggregate
  const [isRaw, setIsRaw] = useState(!aggregation);

  const url = useMemo(() => {
    const queryParams = makeQueryString({
      size: "max",
      tag_id__in: tag_id,
      machine_id__in: machine_id,
      date_format: "epoch",
      explode_value: true
    });

    switch (aggregation) {
      case null: // Raw
        return `scalars_hi/${queryParams}`;
      case ScalarsHiAggregation.DAY:
        return `scalars_hi/day/${queryParams}`;
      case ScalarsHiAggregation.LAST_7_DAYS:
        return `scalars_hi/last_n_days/7/${queryParams}`;
      case ScalarsHiAggregation.LAST_30_days:
        return `scalars_hi/last_n_days/30/${queryParams}`;
      case ScalarsHiAggregation.WEEK:
      case ScalarsHiAggregation.MONTH:
        return `scalars_hi/date_truncations/${aggregation}/${queryParams}`;
      default:
        // No checking in useFetchGet that the URL is not an empty string. Just return the raw data for now
        return `scalars_hi/${queryParams}`;
    }
  }, [aggregation, machine_id, tag_id]);

  useEffect(() => {
    setIsRaw(!aggregation);
  }, [aggregation]);

  const { loading, error, response }: ScalarsHiFetchResponse = useFetchGet(url);

  return {
    loading,
    error,
    data: (response as
      | { results: ScalarsHiDataSlice[]; count: number }
      | undefined)?.results,
    isRaw
  };
};
